import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API, streamSSE } from "@/lib/api";
import { accountHeaders } from "@/lib/account";
import { ideasStore, useIdeas } from "@/lib/storage";
import { Questionnaire, GenerateActions, LimitBanner } from "@/components/GeneratorForm";
import { GeneratorResults } from "@/components/GeneratorResults";

const LAST_KEY = "ideaforge_last";

const useUsage = () => {
  const [usage, setUsage] = useState(null);
  const refresh = useCallback(() => axios.get(`${API}/usage`, { headers: accountHeaders() }).then((r) => setUsage(r.data)).catch(() => {}), []);
  useEffect(() => { refresh(); }, [refresh]);
  return [usage, refresh];
};

const useLastIdeas = () => {
  const [lastIds, setLastIds] = useState(() => JSON.parse(localStorage.getItem(LAST_KEY) || "[]"));
  const all = useIdeas();
  const ideas = lastIds.map((id) => all.find((i) => i.id === id)).filter(Boolean);
  const remember = (newIdeas) => {
    ideasStore.upsert(newIdeas);
    const ids = newIdeas.map((i) => i.id);
    localStorage.setItem(LAST_KEY, JSON.stringify(ids));
    setLastIds(ids);
  };
  return [ideas, remember];
};

export default function Generator() {
  const [params] = useSearchParams();
  const [form, setForm] = useState({ interessi: params.get("interessi") || "" });
  const [streamText, setStreamText] = useState("");
  const [loading, setLoading] = useState(false);
  const [usage, refreshUsage] = useUsage();
  const [ideas, remember] = useLastIdeas();
  const limitReached = usage && !usage.pro && usage.used >= usage.limit;

  const run = async (skip) => {
    setLoading(true);
    setStreamText("");
    try {
      const res = await streamSSE("/ideas/generate", skip ? {} : form, (t) => setStreamText((s) => s + t));
      remember(res.ideas);
      toast.success("3 nuove idee pronte!");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
      refreshUsage();
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14" data-testid="generator-page">
      <div className="grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4">
          <div className="overline-label">Generatore guidato</div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight mt-4 leading-[1.05]">Forgiamo la tua prossima idea.</h1>
          <p className="text-slate-400 mt-5 leading-relaxed">Tutte le domande sono facoltative. Più dettagli dai, più le idee saranno cucite su di te. Oppure salta e l'AI punterà alle più redditizie in assoluto.</p>
        </div>
        <div className="lg:col-span-8 rounded-3xl bg-[#12141F] border border-white/[0.07] p-6 sm:p-8 space-y-7">
          <Questionnaire form={form} setForm={setForm} />
          <GenerateActions run={run} loading={loading} disabled={limitReached} usage={usage} />
          {limitReached && <LimitBanner />}
        </div>
      </div>
      <GeneratorResults loading={loading} streamText={streamText} ideas={ideas} onRegenerate={() => run(false)} />
    </div>
  );
}
